import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ErrorBoundary from 'src/components/ErrorBoundary';
import Navbar from 'src/components/NavBar';
import useWindowSize from 'src/hooks/useWindowSize';
import NotFound from 'src/pages/NotFound';
import PunchoutOrderGuideHeader from 'src/sections/punchout_order_guide/PunchoutOrderGuideHeader';
import PunchoutOrderGuideTable from 'src/sections/punchout_order_guide/PunchoutOrderGuideTable';
import PunchoutSessionInfo from 'src/sections/punchout_order_guide/PunchoutSessionInfo';
import { usePunchoutOrderGuideExternalRedirect } from 'src/sections/punchout_order_guide/usePunchoutRedirect';
import 'src/style/grid/grid-override.scss';

const PunchoutOrderGuide = () => {
  const { t } = useTranslation();
  const { height } = useWindowSize();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');

  const catalogId = useMemo(() => {
    const rawCatalogId = searchParams.get('catalogId');
    if (!rawCatalogId) {
      return null;
    }
    const parsedCatalogId = Number(rawCatalogId);
    return Number.isFinite(parsedCatalogId) ? parsedCatalogId : null;
  }, [searchParams]);

  const hasPunchoutIdentifier = catalogId !== null;

  if (hasPunchoutIdentifier) {
    return (
      <PunchoutOrderGuideContent
        catalogId={catalogId}
        height={height}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        title={t('Punchout Order Guide')}
      />
    );
  }

  return (
    <ErrorBoundary>
      <div
        style={{
          display: 'grid',
          height: `${height}px`,
          gridTemplateRows: 'auto minmax(0, 1fr)',
          gridTemplateColumns: '1fr',
          overflow: 'hidden',
          gridTemplateAreas: `
          "navbar"
          "main"
        `,
        }}
      >
        <div className="mb-2 min-h-0 bg-white" style={{ gridArea: 'navbar' }}>
          <Navbar title={t('Punchout Order Guide')} />
        </div>
        <div
          className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white"
          style={{ gridArea: 'main' }}
        >
          <NotFound />
        </div>
      </div>
    </ErrorBoundary>
  );
};

interface PunchoutOrderGuideContentProps {
  catalogId: number;
  height: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  title: string;
}

const PunchoutOrderGuideContent = ({
  catalogId,
  height,
  searchValue,
  onSearchChange,
  title,
}: PunchoutOrderGuideContentProps) => {
  const { showSessionInfo } = usePunchoutOrderGuideExternalRedirect(catalogId);

  return (
    <ErrorBoundary>
      <div
        style={{
          display: 'grid',
          height: `${height}px`,
          gridTemplateRows: 'auto minmax(0, 1fr)',
          gridTemplateColumns: '1fr',
          overflow: 'hidden',
          gridTemplateAreas: `
          "navbar"
          "main"
        `,
        }}
      >
        <div className="mb-2 min-h-0 bg-white" style={{ gridArea: 'navbar' }}>
          <Navbar title={title} />
          {!showSessionInfo && (
            <PunchoutOrderGuideHeader searchValue={searchValue} onSearchChange={onSearchChange} />
          )}
        </div>
        <div
          className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white"
          style={{ gridArea: 'main' }}
        >
          {showSessionInfo ? (
            <PunchoutSessionInfo />
          ) : (
            <PunchoutOrderGuideTable
              catalogId={catalogId}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default PunchoutOrderGuide;
